import axios from 'axios';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useCreateProductionDeclarationMaterialCommodityOrigin } from '@/services/apis/mutations/production-declaration/use-create-production-declaration-material-commodity-origin';

type UseProductionDeclarationMaterialCommodityOriginCreateParams = {
  materialId: string;
  productionDeclarationId: string;
  token?: string;
};

export function useProductionDeclarationMaterialCommodityOriginCreate({
  materialId,
  productionDeclarationId,
  token,
}: UseProductionDeclarationMaterialCommodityOriginCreateParams) {
  const { t } = useTranslation('productionDeclaration', {
    useSuspense: false,
  });
  const { t: tCommon } = useTranslation('common', {
    useSuspense: false,
  });
  const { mutateAsync: createCommodityOrigin, isPending } =
    useCreateProductionDeclarationMaterialCommodityOrigin();

  const handleCreate = useCallback(async () => {
    if (!(productionDeclarationId && materialId)) {
      return;
    }

    try {
      await createCommodityOrigin({
        productionDeclarationId,
        materialId,
        token,
      });

      window.requestAnimationFrame(() => {
        window.scrollTo({
          behavior: 'smooth',
          top: document.documentElement.scrollHeight,
        });
      });
      toast.success(t('materialCommodityOrigins.createSuccess'));
    } catch (error) {
      const description = axios.isAxiosError(error)
        ? (error.response?.data?.message ??
          error.response?.data?.detail ??
          t('materialCommodityOrigins.createError'))
        : t('materialCommodityOrigins.createError');

      toast.error(tCommon('error'), { description });
    }
  }, [
    createCommodityOrigin,
    materialId,
    productionDeclarationId,
    t,
    tCommon,
    token,
  ]);

  return {
    handleCreate,
    isPending,
  };
}
